import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, Lock, KeyRound, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ChangePassword = () => {
  const navigate = useNavigate();
  const { user, token, updateToken, logout } = useUser();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Validation states
  const hasMinLength = newPassword.length >= 8;
  const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0;
  const isDifferent = newPassword !== currentPassword && newPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validations
    if (!hasMinLength) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    if (!passwordsMatch) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (!isDifferent) {
      setError('La nueva contraseña debe ser diferente a la actual');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Update token in context (new token without force_password_change)
      updateToken(response.data.access_token);
      
      toast.success('Contraseña actualizada correctamente');
      navigate('/');
    } catch (err) {
      const message = err.response?.data?.detail || 'Error al cambiar la contraseña';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <Card className="w-full max-w-md shadow-2xl border-slate-700 bg-slate-800/50 backdrop-blur-sm">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center mb-2">
            <KeyRound className="w-6 h-6 text-amber-500" />
          </div>
          <CardTitle className="text-2xl font-bold text-white">Cambiar Contraseña</CardTitle>
          <CardDescription className="text-slate-400">
            {user?.force_password_change 
              ? 'Debes establecer una nueva contraseña para continuar' 
              : `Hola ${user?.name}, establece tu nueva contraseña`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 text-sm text-red-400 bg-red-900/30 rounded-md border border-red-800" data-testid="change-password-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="currentPassword" className="text-slate-300">Contraseña actual (temporal)</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  id="currentPassword"
                  type="password"
                  placeholder="Contraseña temporal"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 focus:border-primary"
                  required
                  data-testid="current-password"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-slate-300">Nueva contraseña</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  id="newPassword"
                  type="password"
                  placeholder="Mínimo 8 caracteres"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 focus:border-primary"
                  required
                  data-testid="new-password"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-300">Confirmar nueva contraseña</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Repite la nueva contraseña"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 focus:border-primary"
                  required
                  data-testid="confirm-password"
                />
              </div>
            </div>

            {/* Password requirements */}
            <div className="space-y-2 p-3 bg-slate-700/50 rounded-md">
              <p className="text-xs font-medium text-slate-400 mb-2">Requisitos de la contraseña:</p>
              <div className="space-y-1">
                <RequirementItem met={hasMinLength} text="Mínimo 8 caracteres" />
                <RequirementItem met={isDifferent} text="Diferente a la actual" />
                <RequirementItem met={passwordsMatch} text="Las contraseñas coinciden" />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                onClick={handleCancel}
                data-testid="cancel-change-password"
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="flex-1" 
                disabled={loading || !hasMinLength || !passwordsMatch || !isDifferent}
                data-testid="submit-change-password"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Guardando...
                  </span>
                ) : (
                  'Guardar contraseña'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const RequirementItem = ({ met, text }) => (
  <div className={`flex items-center gap-2 text-xs ${met ? 'text-green-400' : 'text-slate-500'}`}>
    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${met ? 'bg-green-500/20' : 'bg-slate-600'}`}>
      {met && <Check className="w-3 h-3" />}
    </div>
    <span>{text}</span>
  </div>
);

export default ChangePassword;
